import { format } from "date-fns";
import {
  currentYearRange,
  defaultSalesDateRange,
  lastYearRange,
  lifetimeRange,
  rangesMatch,
  type SalesDatePresetId,
  type SalesDateRange,
} from "~/shared/sales-data/sales-date-range";
import { DATE_FORMAT, parseInputDate } from "./constants";
import { parseComparisonOperator } from "./comparison-operator-utils";
import { validateCompleteDateRange } from "./date-range-validation";
import { SalesDataFilter, SearchFilterComparisonOperator } from "./types";

export type SalesDataPresetId = SalesDatePresetId;
export type SalesDataMetricKey =
  | "totalUnits"
  | "amazonOrderedUnits"
  | "amazonConversionRate";

export interface SalesDataMetricDefinition {
  key: SalesDataMetricKey;
  operatorPrefix: string;
  analyticsValue: string;
  percentage?: boolean;
}

export interface ActiveSalesDataMetric {
  key: SalesDataMetricKey;
  comparisonOperator: SearchFilterComparisonOperator;
  value: number;
  definition: SalesDataMetricDefinition;
}

export interface SalesDataRange {
  from: string;
  to: string;
}

export interface ParsedSalesDataRange {
  from?: Date;
  to?: Date;
}

export const emptySalesDataRange: SalesDataRange = { from: "", to: "" };

export const formatSalesDataInputDate = (date: Date): string =>
  format(date, DATE_FORMAT);

const parseSalesDataInputDate = parseInputDate;

export const parseSalesDataRange = (
  range: SalesDataRange,
): ParsedSalesDataRange => ({
  from: range.from ? parseSalesDataInputDate(range.from) : undefined,
  to: range.to ? parseSalesDataInputDate(range.to) : undefined,
});

const hasSalesDataMetricValue = (value: number | undefined): value is number =>
  value !== undefined && !Number.isNaN(value) && value >= 0;

export const salesDataMetricDefinitions: SalesDataMetricDefinition[] = [
  {
    key: "totalUnits",
    operatorPrefix: "units",
    analyticsValue: "total_units",
  },
  {
    key: "amazonOrderedUnits",
    operatorPrefix: "amazon_units",
    analyticsValue: "amazon_ordered_units",
  },
  {
    key: "amazonConversionRate",
    operatorPrefix: "amazon_conversion_rate",
    analyticsValue: "amazon_conversion_rate",
    percentage: true,
  },
];

export const hasValidSalesDataMetricValue = (
  metricKey: SalesDataMetricKey,
  value: number | undefined,
): value is number => {
  const definition = salesDataMetricDefinitions.find(
    (metric) => metric.key === metricKey,
  );
  return Boolean(
    definition &&
      hasSalesDataMetricValue(value) &&
      (!definition.percentage || value <= 100),
  );
};

export const formatSalesDataMetricFilterOperator = (
  metricKey: SalesDataMetricKey,
  operator: SearchFilterComparisonOperator,
): string => {
  const definition = salesDataMetricDefinitions.find(
    (metric) => metric.key === metricKey,
  );
  if (!definition) {
    throw new Error(`Unhandled sales data metric: ${metricKey}`);
  }

  return `${definition.operatorPrefix}_${operator}`;
};

export const parseSalesDataMetricFilterOperator = (
  operator: string,
):
  | {
      metricKey: SalesDataMetricKey;
      comparisonOperator: SearchFilterComparisonOperator;
    }
  | undefined => {
  for (const definition of salesDataMetricDefinitions) {
    const prefix = `${definition.operatorPrefix}_`;
    if (operator.startsWith(prefix)) {
      const comparisonOperator = operator.slice(prefix.length);
      if (
        Object.values(SearchFilterComparisonOperator).includes(
          comparisonOperator as SearchFilterComparisonOperator,
        )
      ) {
        return {
          metricKey: definition.key,
          comparisonOperator:
            comparisonOperator as SearchFilterComparisonOperator,
        };
      }

      return undefined;
    }
  }

  return undefined;
};

export const getActiveSalesDataMetrics = (
  filter: SalesDataFilter | null | undefined,
): ActiveSalesDataMetric[] => {
  if (!filter) {
    return [];
  }

  return salesDataMetricDefinitions.flatMap((definition) => {
    const metricFilter = filter[definition.key];
    if (!hasValidSalesDataMetricValue(definition.key, metricFilter?.value)) {
      return [];
    }

    return [
      {
        key: definition.key,
        comparisonOperator:
          metricFilter.comparisonOperator ??
          SearchFilterComparisonOperator.GREATER,
        value: metricFilter.value,
        definition,
      },
    ];
  });
};

export const isActiveSalesDataFilter = (
  filter: SalesDataFilter | null | undefined,
  maxDate: Date,
): filter is SalesDataFilter =>
  Boolean(
    filter &&
      getActiveSalesDataMetrics(filter).length > 0 &&
      Boolean(filter.range.from) === Boolean(filter.range.to) &&
      validateCompleteDateRange(filter.range, { maxDate }).isValid,
  );

const salesDataPresetIds: SalesDataPresetId[] = [
  "last-12-weeks",
  "current-year",
  "last-year",
  "lifetime",
];

const buildSalesDatePresetRange = (
  presetId: SalesDataPresetId,
  referenceDate = new Date(),
): SalesDateRange => {
  switch (presetId) {
    case "last-12-weeks":
      return defaultSalesDateRange(referenceDate);
    case "current-year":
      return currentYearRange(referenceDate);
    case "last-year":
      return lastYearRange(referenceDate);
    case "lifetime":
      return lifetimeRange(referenceDate);
    default:
      throw new Error(`Unhandled sales data preset: ${presetId}`);
  }
};

export const buildSalesDataPresetRange = (
  presetId: SalesDataPresetId,
  referenceDate = new Date(),
): SalesDataRange => {
  const range = buildSalesDatePresetRange(presetId, referenceDate);
  return {
    from: formatSalesDataInputDate(range.start),
    to: formatSalesDataInputDate(range.end),
  };
};

export const getActiveSalesDataPresetId = (
  range: SalesDataRange,
  referenceDate = new Date(),
): SalesDataPresetId | undefined => {
  const parsed = parseSalesDataRange(range);
  const { from, to } = parsed;
  if (!from || !to) {
    return undefined;
  }

  return salesDataPresetIds.find((presetId) =>
    rangesMatch(
      { start: from, end: to },
      buildSalesDatePresetRange(presetId, referenceDate),
    ),
  );
};

export const formatSalesDataSummary = (
  filter: SalesDataFilter,
  metricLabels: Record<SalesDataMetricKey, string>,
  formatMetricsCount: (count: number) => string,
): string | null => {
  const activeMetrics = getActiveSalesDataMetrics(filter);
  if (!activeMetrics.length) {
    return null;
  }

  if (activeMetrics.length > 1) {
    return formatMetricsCount(activeMetrics.length);
  }

  const [metric] = activeMetrics;
  const value = metric.definition.percentage
    ? `${metric.value}%`
    : String(metric.value);

  return `${metricLabels[metric.key]} ${parseComparisonOperator(metric.comparisonOperator)} ${value}`;
};
